/* Tracking Event client data model
 *
 * Angular Module: trackerbase-app.models.tracking-event
 * Injected Factory: TrackingEvent
 */

import moment from "moment-timezone";
import angular from "angular";

import State from "./State";
import EventType from "./EventType";
import Tracker from "./Tracker";

import makePropsEnumerable from "../utility/MakePropsEnumerable";

//All States Service dependency;
const mAllStatesService = new WeakMap();

//All Tracking Statuses dependency;
const mAllTrackingStatusesSvc = new WeakMap();

//Utility RE for generating event display names
const mDisplayNameMatchRE = /^\s*?[A-Z]{2}\s*-\s*(.*?)\s*(?:\(?\d{1,2}\/\d{1,2}\/\d{1,4}\)?\s*)+$/;

class TrackingEvent {
    constructor(
        allStatesService,
        allTrackingStatusesService,
        eventId,
        eventName,
        eventStart,
        eventEnd,
        eventTimezone,
        description,
        notes,
        pressDetails,
        status,
        isInternal,
        isExternal,
        locationName,
        locationAddress,
        locationCity,
        locationState,
        workflowNextSteps,
        eventTypes,
        eventTrackers,
        attendees,
        recurrenceId,
        recurrenceRule,
        recurrenceException,
        isAllDay
    ) {
        if (allStatesService != null) {
            mAllStatesService.set(this, allStatesService);
        }
        else {
            throw new Error("Missing Required All States Service Dependency");
        }

        if (allTrackingStatusesService != null) {
            mAllTrackingStatusesSvc.set(this, allTrackingStatusesService);
        }
        else {
            throw new Error("Missing Required All Tracking Statuses Service Dependency");
        }

        if (Number.isInteger(eventId)) {
            this.trackingEventId = eventId;
        }
        else {
            throw new TypeError("Invalid or missing event id.");
        }

        if (angular.isString(eventName)) {
            this.eventName = eventName;
        }
        else {
            throw new TypeError("Invalid or missing event name.");
        }

        if (eventTimezone != null) {
            this.eventTimezone = eventTimezone;
        } else {
            this.eventTimezone = "UTC";
        }

        if (eventStart !== undefined) {
            let evtStart = moment.tz(eventStart, this.eventTimezone);
            if (evtStart.isValid()) {
                this.eventStart = evtStart;
            } else {
                throw new TypeError("Invalid start date-time.");
            }
        }
        else {
            throw new TypeError("Invalid or missing start date-time.");
        }

        if (eventEnd !== undefined) {
            let evtEnd = moment.tz(eventEnd, this.eventTimezone);
            if ((evtEnd.isValid()) &&
                ((this.eventStart.isBefore(evtEnd)) ||
                (this.eventStart.isSame(evtEnd)))
            ) {
                this.eventEnd = evtEnd;
            } else {
                throw new TypeError("Invalid end date-time.");
            }
        }
        else {
            throw new TypeError("Invalid or missing end date-time.");
        }

        if (description != null) {
            this.description = description;
        } else {
            this.description = "";
        }

        if (notes != null) {
            this.notes = notes;
        } else {
            this.notes = "";
        }

        if (pressDetails != null) {
            this.pressDetails = pressDetails;
        } else {
            this.pressDetails = "";
        }

        //TODO: Validate Status Names By Creating A Status Object (like State)
        if ((angular.isString(status)) && (status.length > 0)) {
            this.statusName = status;
        }
        else {
            this.statusName = "Proposed";
        }

        const evtIsInternal = (isInternal === true);
        const evtIsExternal = (isExternal === true);
        if (evtIsInternal && evtIsExternal) {
            throw new TypeError("event: isInternal and isExternal cannot both be true");
        }
        this._isInternal = evtIsInternal;
        this._isExternal = evtIsExternal;

        this.locationName = locationName;
        this.locationAddress = locationAddress;
        this.locationCity = locationCity;
        this.locationState = State.CopyState(locationState);

        if ((angular.isDefined(workflowNextSteps)) &&
            (angular.isArray(workflowNextSteps))) {
            this.workflowTransitions = angular.copy(workflowNextSteps);
        }
        else {
            this.workflowTransitions = [];
        }

        this.eventTypes = eventTypes.map(function (et) {
            return EventType.Copy(et);
        });

        this.eventTrackers = eventTrackers.map(function(eTracker) {
            return Tracker.CopyTracker(eTracker);
        });

        this.eventAttendees = attendees;

        //kendo scheduling items that we don"t really care about.
        this.recurrenceId = recurrenceId;
        this.recurrenceRule = recurrenceRule;
        this.recurrenceException = recurrenceException;
        this.isAllDay = isAllDay;

    }

    get EventId() {
        return this.trackingEventId;
    }

    get eventTransitions() {
        return this.workflowTransitions;
    }

    get StateName() {
        return this.locationState.shortName;
    }

    set StateName(stName) {

        let state = mAllStatesService.get(this).FromShortName(stName);
        if (angular.isDefined(state)) {
            this.locationState = state;
        }
        else {
            throw new TypeError(
                "Invalid state name or state data not loaded"
            );
        }

    }

    get StateId() {
        return this.locationState.stateId;
    }

    set StateId(stId) {

        let state = mAllStatesService.get(this).FromId(stId);
        if (angular.isDefined(state)) {
            this.locationState = state;
        }
        else {
            throw new TypeError(
                "Invalid state name or state data not loaded"
            );
        }

    }

    setStartMoment(startMoment) {
        if (!moment.isMoment(startMoment)) {
            throw new TypeError("Not a start moment");
        }

        if (!startMoment.isValid()) {
            throw new TypeError("Invalid start moment");
        }

        let currentEventDuration = this.eventEnd.diff(this.eventStart);

        let updatedEventEnd = startMoment.clone().add(currentEventDuration);

        this.eventStart = startMoment;
        this.eventEnd = updatedEventEnd;
    }

    setEndMoment(eMoment) {
        if (!moment.isMoment(eMoment)) {
            throw new TypeError("Not an end moment");
        }

        if (!eMoment.isValid()) {
            throw new TypeError("Invalid end moment");
        }

        if ((this.eventStart.isBefore(eMoment)) || (this.eventStart.isSame(eMoment))) {
            this.eventEnd = eMoment;
        }

        //We implicitly reject any value that didn't pass validation.
    }

    get startDate() {
        return this.eventStart.toDate();
    }

    set startDate(nStartDt) {
        let evtStart = null;

        if (angular.isDate(nStartDt)) {
            evtStart = moment(nStartDt);
        }
        else if (angular.isString(nStartDt)) {
            evtStart = moment(new Date(nStartDt));
        }
        else {
            throw new TypeError("Invalid start date data format.");
        }

        //For start date-time changes, adjust the end date-time to maintain
        //the current event duration.

        this.setStartMoment(evtStart);
    }

    get startDateVal() {
        return this.eventStart.format(TrackingEvent.getShortFormat());
    }

    set startDateVal(dateStr) {
        let sMoment = moment.tz(dateStr, TrackingEvent.getShortFormat(), this.eventTimezone);
        this.setStartMoment(sMoment);
    }

    get start() {
        return this.eventStart.clone().toDate();
    }

    set start(nStartDt) {
        this.startDate = nStartDt;
    }

    get endDate() {
        return this.eventEnd.toDate();
    }

    set endDate(nEndDt) {

        if ((angular.isUndefined(nEndDt)) || (nEndDt == null)) {
            // effectively reject the proposed change.
            return;
        }

        let evtEnd = null;
        if (angular.isDate(nEndDt)) {
            evtEnd = moment(nEndDt);
        }
        else if (angular.isString(nEndDt)) {
            evtEnd = moment(new Date(nEndDt));
        }
        else {
            throw new TypeError("Wrong type for endDate");
        }

        this.setEndMoment(evtEnd);
    }

    get end() {
        return this.eventEnd.clone().toDate();
    }

    set end(nEndDt) {
        this.endDate = nEndDt;
    }

    get endDateVal() {
        return this.eventEnd.format(TrackingEvent.getShortFormat());
    }

    set endDateVal(dateStr) {
        let eMoment = moment.tz(dateStr, TrackingEvent.getShortFormat(), this.eventTimezone);
        this.setEndMoment(eMoment);
    }

    get tzVal() {
        return this.eventTimezone;
    }

    set tzVal(tzValStr) {
        this.eventStart.tz(tzValStr);
        this.eventEnd.tz(tzValStr);
        this.eventTimezone = tzValStr;
    }

    get eventDisplayName() {
        return this.getEventDisplayName();
    }

    get trackingStatus() {
        return mAllTrackingStatusesSvc.get(this).getFromName(this.statusName);
    }

    get isInternal() {
        return this._isInternal;
    }

    set isInternal(newVal) {
        if (!(this.isExternal && newVal)) {
            this._isInternal = newVal;
        }
        else {
            throw new TypeError("Cannot set isInternal to true when event is marked as external-only");
        }
    }

    get isExternal() {
        return this._isExternal;
    }

    set isExternal(newVal) {
        if (!(this.isInternal && newVal)) {
            this._isExternal = newVal;
        }
        else {
            throw new TypeError("Cannot set isExternal to true when event is marked as internal-only");
        }
    }

    getEventDisplayName() {
        if (this.eventName == null) {
            return "";
        }

        let reResult = this.eventName.match(
            mDisplayNameMatchRE
        );

        if (reResult != null) {
            let displayName = reResult[1];
            if (displayName.length > 0) {
                return displayName;
            }
            else {
                return this.eventName;
            }
        }
        else {
            return this.eventName;
        }
    }

    isCoreDataEqual(otherEvent) {
        if (otherEvent == null) {
            return false;
        }

        const baseResult = (
            (this.trackingEventId === otherEvent.trackingEventId) &&
            (this.eventName === otherEvent.eventName) &&
            (this.eventTimezone === otherEvent.eventTimezone) &&
            (this.eventStart.isSame(otherEvent.eventStart)) &&
            (this.eventEnd.isSame(otherEvent.eventEnd)) &&
            (this.description === otherEvent.description) &&
            (this.notes === otherEvent.notes) &&
            (this.pressDetails === otherEvent.pressDetails) &&
            (this.statusName === otherEvent.statusName) &&
            (this._isInternal === otherEvent._isInternal) &&
            (this._isExternal === otherEvent._isExternal) &&
            (this.locationName === otherEvent.locationName) &&
            (this.locationAddress === otherEvent.locationAddress) &&
            (this.locationCity === otherEvent.locationCity));

        if (!baseResult) {
            return false;
        }

        // for location state compare, need to guard against locationState being null/undefined.
        let stateCompare = false;
        if (this.locationState == null) {
            stateCompare = (otherEvent.locationState == null);
        }
        else {
            stateCompare = (otherEvent.locationState != null) && (this.locationState.stateId === otherEvent.locationState.stateId);
        }

        return stateCompare;
    }


    hasAvailableWorkflowTransitions() {
        return (this.workflowTransitions.length > 0);
    }

    toString() {
        return `[${this.eventStart.format("lll")} - ${this.eventEnd.format("lll")}] ${this.eventName} (${this.locationCity}, ${this.locationState.ShortName})`;
    }

    valueOf() {
        return this.trackingEventId;
    }

    toServerUpdateEventRequest() {
        return {
            TrackingEventId: this.trackingEventId,
            Name: this.eventName,
            EventStart: this.eventStart,
            StartTimezone: this.eventTimezone,
            EventEnd: this.eventEnd,
            EndTimezone: this.eventTimezone,
            Description: this.description,
            Notes: this.notes,
            PressDetails: this.pressDetails,
            IsInternal: this.isInternal,
            IsExternal: this.isExternal,
            Location_Name: this.locationName,
            Location_Address: this.locationAddress,
            Location_City: this.locationCity,
            Location_StateId: this.locationState.stateId,
            RecurrenceID: this.recurrenceId,
            RecurrenceRule: this.recurrenceRule,
            RecurrenceException: this.recurrenceException,
            IsAllDay: this.isAllDay
        };
    }

    toServerCreateEventRequest() {
        return {
            Name: this.eventName,
            EventStart: this.eventStart,
            EventEnd: this.eventEnd,
            StartTimezone: this.eventTimezone,
            EndTimezone: this.eventTimezone,
            Location_Name: this.locationName,
            Location_Address: this.locationAddress,
            Location_City: this.locationCity,
            Location_StateId: this.locationState.stateId,
            IsInternal: this.isInternal,
            IsExternal: this.isExternal,
            Notes: this.notes,
            PressDetails: this.pressDetails,
            Description: this.description
        };
    }

    toSchedulerEventModel() {
        return {
            trackingEventId: this.trackingEventId,
            title: this.eventName,
            displayTitle: this.getEventDisplayName(),
            start: this.start,
            eventTimezone: this.eventTimezone,
            end: this.end,
            recurrenceId: this.recurrenceId,
            recurrenceRule: this.recurrenceRule,
            recurrenceException: this.recurrenceException,
            isAllDay: this.isAllDay,
            isInternal: this.isInternal,
            isExternal: this.isExternal,
            statusName: this.statusName,
            locationName: this.locationName,
            locationAddress: this.locationAddress,
            locationCity: this.locationCity,
            locationStateId: this.StateId,
            locationStateName: this.StateName,
            description: this.description,
            notes: this.notes,
            pressDetails: this.pressDetails,
            availableTransitions: this.workflowTransitions,
            eventTypes: this.eventTypes,
            eventAttendees: this.eventAttendees,
            eventTrackers: this.eventTrackers
        };
    }

    static getLongFormat() {
        return "MMMM D, YYYY h:mm A";
    }

    static getShortFormat() {
        return "M/D/YYYY h:mm A";
    }

    static Create(allStatesService, allTrackingStatusesService, trackingTargetFactoryService) {
        return (
            (eventId,
             eventName,
             eventStart,
             eventEnd,
             eventTimezone,
             description,
             notes,
             pressDetails,
             status,
             isInternal,
             isExternal,
             locationName,
             locationAddress,
             locationCity,
             locationState,
             workflowNextSteps,
             eventTypes,
             eventTrackers,
             attendees,
             recurrenceId,
             recurrenceRule,
             recurrenceException,
             isAllDay
            ) => new TrackingEvent(
                    allStatesService,
                    allTrackingStatusesService,
                    eventId,
                    eventName,
                    eventStart,
                    eventEnd,
                    eventTimezone,
                    description,
                    notes,
                    pressDetails,
                    status,
                    isInternal,
                    isExternal,
                    locationName,
                    locationAddress,
                    locationCity,
                    locationState,
                    workflowNextSteps,
                    eventTypes,
                    eventTrackers,
                    attendees.map(tt => trackingTargetFactoryService.CopyTarget(tt)),
                    recurrenceId,
                    recurrenceRule,
                    recurrenceException,
                    isAllDay)
        );
    }



}

makePropsEnumerable(TrackingEvent.prototype, [
    "EventId",
    "eventTransitions",
    "StateId",
    "StateName",
    "setStartMoment",
    "endStartMoment",
    "startDate",
    "startDateVal",
    "start",
    "endDate",
    "endDateVal",
    "end",
    "tzVal",
    "isInternal",
    "isExternal",
    "eventDisplayName",
    "trackingStatus",
    "getEventDisplayName",
    "hasAvailableWorkflowTransitions",
    "toServerUpdateEventRequest",
    "toServerCreateEventRequest",
    "toSchedulerEventModel"
]);

export default TrackingEvent;




//////////////////
// WEBPACK FOOTER
// ./src/models/TrackingEvent.js
// module id = t7WN
// module chunks = 1